// M6.2 — /app/builder. Form-driven UTM URL builder that emits links the
// canonical taxonomy recognises. Live URL preview + live band chip mean
// the user sees "excellent" as soon as the fields are canonical.
//
// Reads ?preset (fetch + prefill), ?medium and ?source (prefill only) from
// the URL so the canonical-naming recommendation card on /app/report and
// /app/utm-health can deep-link here.
//
// Save-preset flow: POST /api/presets. Presets are per-user; the list is
// loaded on mount and the "Load preset" dropdown replaces the form state
// when the user picks one.

import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { buildUtmUrl, canonicalizeSource } from "@shared/utm/build";
import { scoreRows } from "@shared/utm/score";
import { CANONICAL_MEDIUMS, CANONICAL_PLATFORMS } from "@shared/utm/types";
import {
  canonicaliseValue,
  evaluateCampaignStructure,
  evaluateGlobalFormat,
  evaluateVocabulary,
  loadRuleset,
  type RuleHit,
} from "@shared/utm/naming-conventions";
import { fetchBootstrap, type BootstrapResponse } from "@/lib/api";
import { withBase } from "@/lib/base-path";
import { usePageMeta } from "@/hooks/usePageMeta";

interface FormState {
  baseUrl: string;
  source: string;
  medium: string;
  campaign: string;
  content: string;
  term: string;
  sourcePlatform: string;
}

interface PresetSummary {
  id: string;
  name: string;
  config: {
    baseUrl: string;
    source?: string;
    medium?: string;
    campaign?: string;
    content?: string;
    term?: string;
    sourcePlatform?: string;
  };
  createdAt: number;
  updatedAt: number;
}

// Builder is a content-only tool — URL building happens client-side
// against the canonical taxonomy import. Authed users get the
// presets-list + Save-as-preset affordances; anon users see the
// builder form alone with a hint to sign in for presets.
type Status = { kind: "loading" } | { kind: "ready"; bootstrap: BootstrapResponse | null };

const EMPTY: FormState = {
  baseUrl: "",
  source: "",
  medium: "organic-social",
  campaign: "",
  content: "",
  term: "",
  sourcePlatform: "",
};

export function BuilderPage() {
  usePageMeta({ title: "UTM builder · Traffic Snapshot" });
  const [params] = useSearchParams();
  const [status, setStatus] = useState<Status>({ kind: "loading" });
  const [form, setForm] = useState<FormState>({
    ...EMPTY,
    medium: params.get("medium") ?? EMPTY.medium,
    source: params.get("source") ?? EMPTY.source,
  });
  const [presets, setPresets] = useState<PresetSummary[]>([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const bootstrap = await fetchBootstrap();
        if (cancelled) return;
        if (!bootstrap.authenticated) {
          // Anon mode — render the builder form with no presets affordances.
          setStatus({ kind: "ready", bootstrap: null });
          return;
        }
        setStatus({ kind: "ready", bootstrap });
        // Load presets after auth confirmed.
        try {
          const res = await fetch(withBase("/api/presets"), { credentials: "include" });
          if (res.ok) {
            const body = (await res.json()) as { presets: PresetSummary[] };
            if (!cancelled) setPresets(body.presets);
          }
        } catch {
          /* non-fatal */
        }
      } catch {
        if (!cancelled) setStatus({ kind: "ready", bootstrap: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Prefill from a preset id in the URL — runs after presets load.
  const presetId = params.get("preset");
  useEffect(() => {
    if (!presetId) return;
    const p = presets.find((x) => x.id === presetId);
    if (!p) return;
    setForm({
      baseUrl: p.config.baseUrl ?? "",
      source: p.config.source ?? "",
      medium: p.config.medium ?? "organic-social",
      campaign: p.config.campaign ?? "",
      content: p.config.content ?? "",
      term: p.config.term ?? "",
      sourcePlatform: p.config.sourcePlatform ?? "",
    });
  }, [presetId, presets]);

  const preview = useMemo(() => {
    if (!form.baseUrl.trim()) return null;
    try {
      return buildUtmUrl(form.baseUrl.trim(), {
        source: form.source,
        medium: form.medium,
        campaign: form.campaign,
        content: form.content,
        term: form.term,
        sourcePlatform: form.sourcePlatform,
      });
    } catch {
      return null;
    }
  }, [form]);

  const liveScore = useMemo(() => {
    if (!preview) return null;
    return scoreRows([
      {
        url: preview,
        source: form.source,
        medium: form.medium,
        campaign: form.campaign,
        content: form.content,
        term: form.term,
        sourcePlatform: form.sourcePlatform,
      },
    ]);
  }, [preview, form]);

  const canonicalSource = form.source ? canonicalizeSource(form.source) : null;

  // Naming-convention evaluators run on every keystroke so the user sees
  // formatting / vocabulary / anti-pattern violations the moment they
  // type a value. The same evaluators run server-side inside `scoreRows`,
  // so the pills here mirror what would land on the audit. Each hit
  // optionally carries a `rewriteHint` ("Google -> google") which the
  // pill turns into a one-click apply.
  const ruleset = useMemo(() => loadRuleset(), []);
  const fieldHits = useMemo(() => {
    type Field = keyof Pick<FormState, "source" | "medium" | "campaign" | "content" | "term">;
    const hits: Record<Field, RuleHit[]> = {
      source: [],
      medium: [],
      campaign: [],
      content: [],
      term: [],
    };
    for (const [value, field] of [
      [form.source, "source"],
      [form.medium, "medium"],
      [form.campaign, "campaign"],
      [form.content, "content"],
      [form.term, "term"],
    ] as Array<[string, Field]>) {
      hits[field].push(...evaluateGlobalFormat(value, field, ruleset));
    }
    if (form.source) hits.source.push(...evaluateVocabulary(form.source, "source", ruleset));
    if (form.medium) hits.medium.push(...evaluateVocabulary(form.medium, "medium", ruleset));
    if (form.campaign) hits.campaign.push(...evaluateCampaignStructure(form.campaign, ruleset));
    return hits;
  }, [form, ruleset]);

  const totalHits =
    fieldHits.source.length +
    fieldHits.medium.length +
    fieldHits.campaign.length +
    fieldHits.content.length +
    fieldHits.term.length;

  // Apply a single hit's rewrite (if any) to the matching field.
  function applyHit(field: keyof FormState, hit: RuleHit): void {
    if (!hit.rewriteHint) return;
    const [, rewritten] = hit.rewriteHint.split(" -> ");
    if (!rewritten) return;
    setForm((prev) => ({ ...prev, [field]: rewritten }));
  }

  // Fix all — canonicalise each field that has at least one formatting
  // hit. Vocabulary / anti-pattern hits aren't auto-fixable (no
  // mechanical rewrite), so the user resolves those manually.
  function fixAll(): void {
    setForm((prev) => ({
      ...prev,
      source: prev.source ? canonicaliseValue(prev.source) : prev.source,
      campaign: prev.campaign ? canonicaliseValue(prev.campaign) : prev.campaign,
      content: prev.content ? canonicaliseValue(prev.content) : prev.content,
      term: prev.term ? canonicaliseValue(prev.term) : prev.term,
      // medium comes from a <select> bound to CANONICAL_MEDIUMS so it's
      // never a formatting violation; leave as-is.
    }));
  }

  const isAuthed = status.kind === "ready" && status.bootstrap !== null;
  const property = status.kind === "ready" ? (status.bootstrap?.defaultProperty ?? null) : null;

  async function onSavePreset() {
    setSaveError(null);
    const name = saveName.trim();
    if (!name) {
      setSaveError("Give this preset a name so you can find it again.");
      return;
    }
    if (!form.baseUrl.trim()) {
      setSaveError("Enter a base URL before saving.");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(withBase("/api/presets"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name,
          config: {
            baseUrl: form.baseUrl.trim(),
            source: form.source || undefined,
            medium: form.medium || undefined,
            campaign: form.campaign || undefined,
            content: form.content || undefined,
            term: form.term || undefined,
            sourcePlatform: form.sourcePlatform || undefined,
          },
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        setSaveError(body?.error ?? `Couldn't save (${res.status}).`);
        return;
      }
      const body = (await res.json()) as { preset: PresetSummary };
      setPresets((prev) => [body.preset, ...prev]);
      setSaveOpen(false);
      setSaveName("");
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function onCopy() {
    if (!preview) return;
    try {
      await navigator.clipboard.writeText(preview);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore — browser refused or no permission */
    }
  }

  return (
    <AppShell
      property={property ?? null}
      eyebrow="UTM builder"
      title={
        <>
          Tag a <em className="font-serif text-purple-700">link</em>
        </>
      }
      subtitle="Fill in the fields — we'll emit a URL that scores 'excellent' on the same taxonomy the audit uses."
    >
      <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <FormRow label="Base URL" htmlFor="base-url" required>
            <input
              id="base-url"
              name="baseUrl"
              type="url"
              required
              placeholder="https://example.com/pricing"
              value={form.baseUrl}
              onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
              className="w-full rounded-md bg-white px-3 py-2 text-sm text-neutral-900 ring-1 ring-neutral-950/10 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </FormRow>

          <div className="grid gap-5 md:grid-cols-2">
            <FormRow label="Source" htmlFor="source" required>
              <input
                id="source"
                name="source"
                required
                placeholder="instagram"
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                className="w-full rounded-md bg-white px-3 py-2 text-sm text-neutral-900 ring-1 ring-neutral-950/10 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              {form.source && canonicalSource ? (
                <p className="mt-1 text-xs text-green-700">
                  Canonical: <span className="font-semibold">{canonicalSource}</span>
                </p>
              ) : form.source ? (
                <p className="mt-1 text-xs text-amber-700">
                  Unknown source — will count as <span className="font-semibold">Other Social</span>
                  .
                </p>
              ) : null}
              <FieldPills field="source" hits={fieldHits.source} onApply={applyHit} />
            </FormRow>

            <FormRow label="Medium" htmlFor="medium" required>
              <div className="inline-grid w-full grid-cols-[1fr_--spacing(8)]">
                <select
                  id="medium"
                  name="medium"
                  required
                  value={form.medium}
                  onChange={(e) => setForm({ ...form, medium: e.target.value })}
                  className="col-span-full row-start-1 appearance-none rounded-md bg-white py-2 pr-8 pl-3 text-sm text-neutral-900 ring-1 ring-neutral-950/10 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {CANONICAL_MEDIUMS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                <svg
                  viewBox="0 0 8 5"
                  width="8"
                  height="5"
                  fill="none"
                  aria-hidden="true"
                  className="pointer-events-none col-start-2 row-start-1 place-self-center"
                >
                  <path d="M.5.5 4 4 7.5.5" stroke="currentcolor" />
                </svg>
              </div>
              <FieldPills field="medium" hits={fieldHits.medium} onApply={applyHit} />
            </FormRow>
          </div>

          <FormRow label="Campaign" htmlFor="campaign" required>
            <input
              id="campaign"
              name="campaign"
              required
              placeholder="q1-free-trial"
              value={form.campaign}
              onChange={(e) => setForm({ ...form, campaign: e.target.value })}
              className="w-full rounded-md bg-white px-3 py-2 text-sm text-neutral-900 ring-1 ring-neutral-950/10 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            {form.campaign && form.campaign.trim().length < 4 ? (
              <p className="mt-1 text-xs text-amber-700">
                Short campaign names hurt groupability. Aim for q1-free-trial / apr-newsletter.
              </p>
            ) : null}
            <FieldPills field="campaign" hits={fieldHits.campaign} onApply={applyHit} />
          </FormRow>

          <details className="rounded-md bg-white p-4 ring-1 ring-neutral-950/5">
            <summary className="cursor-pointer select-none text-sm font-medium text-neutral-700">
              Add content / term / source platform
            </summary>
            <div className="mt-4 space-y-5">
              <FormRow label="Content" htmlFor="content">
                <input
                  id="content"
                  name="content"
                  placeholder="reel-01"
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  className="w-full rounded-md bg-white px-3 py-2 text-sm text-neutral-900 ring-1 ring-neutral-950/10 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <FieldPills field="content" hits={fieldHits.content} onApply={applyHit} />
              </FormRow>
              <FormRow label="Term" htmlFor="term">
                <input
                  id="term"
                  name="term"
                  placeholder="(paid keyword)"
                  value={form.term}
                  onChange={(e) => setForm({ ...form, term: e.target.value })}
                  className="w-full rounded-md bg-white px-3 py-2 text-sm text-neutral-900 ring-1 ring-neutral-950/10 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <FieldPills field="term" hits={fieldHits.term} onApply={applyHit} />
              </FormRow>
              <FormRow label="Source platform" htmlFor="sourcePlatform">
                <div className="inline-grid w-full grid-cols-[1fr_--spacing(8)]">
                  <select
                    id="sourcePlatform"
                    name="sourcePlatform"
                    value={form.sourcePlatform}
                    onChange={(e) => setForm({ ...form, sourcePlatform: e.target.value })}
                    className="col-span-full row-start-1 appearance-none rounded-md bg-white py-2 pr-8 pl-3 text-sm text-neutral-900 ring-1 ring-neutral-950/10 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">(none)</option>
                    {CANONICAL_PLATFORMS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                  <svg
                    viewBox="0 0 8 5"
                    width="8"
                    height="5"
                    fill="none"
                    aria-hidden="true"
                    className="pointer-events-none col-start-2 row-start-1 place-self-center"
                  >
                    <path d="M.5.5 4 4 7.5.5" stroke="currentcolor" />
                  </svg>
                </div>
              </FormRow>
            </div>
          </details>

          <div className="flex flex-wrap items-center gap-3">
            {totalHits > 0 ? (
              <Button
                type="button"
                variant="outline"
                onClick={fixAll}
                title="Apply mechanical rewrites (lowercase, hyphens, drop disallowed chars) across every field"
              >
                <i className="fa-regular fa-wand-magic-sparkles text-xs" aria-hidden="true" />
                Fix all ({totalHits})
              </Button>
            ) : null}
            {isAuthed ? (
              <div className="flex flex-col items-start gap-1">
                <Button
                  type="button"
                  onClick={() => setSaveOpen(true)}
                  disabled={!preview}
                  className="ts-cta"
                >
                  <i className="fa-regular fa-floppy-disk text-xs" aria-hidden="true" />
                  Save as preset
                </Button>
                {!preview ? (
                  <p className="text-xs text-neutral-500">Enter a Base URL to save this preset.</p>
                ) : null}
              </div>
            ) : (
              <p className="text-xs text-neutral-500">
                <Link
                  to="/app/connect?return_to=%2Fapp%2Fbuilder"
                  className="text-purple-500 underline-offset-2 hover:underline"
                >
                  Sign in
                </Link>{" "}
                to save presets and pull them back into future links.
              </p>
            )}
            {presets.length > 0 ? (
              <label className="inline-grid grid-cols-[1fr_--spacing(8)] items-center">
                <span className="sr-only">Load preset</span>
                <select
                  onChange={(e) => {
                    const p = presets.find((x) => x.id === e.target.value);
                    if (p) {
                      setForm({
                        baseUrl: p.config.baseUrl ?? "",
                        source: p.config.source ?? "",
                        medium: p.config.medium ?? "organic-social",
                        campaign: p.config.campaign ?? "",
                        content: p.config.content ?? "",
                        term: p.config.term ?? "",
                        sourcePlatform: p.config.sourcePlatform ?? "",
                      });
                    }
                  }}
                  value=""
                  className="col-span-full row-start-1 appearance-none rounded-md bg-white py-2 pr-8 pl-3 text-sm text-neutral-900 ring-1 ring-neutral-950/10 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Load preset…</option>
                  {presets.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <svg
                  viewBox="0 0 8 5"
                  width="8"
                  height="5"
                  fill="none"
                  aria-hidden="true"
                  className="pointer-events-none col-start-2 row-start-1 place-self-center"
                >
                  <path d="M.5.5 4 4 7.5.5" stroke="currentcolor" />
                </svg>
              </label>
            ) : null}
          </div>
        </form>

        <aside className="space-y-4">
          <div className="rounded-2xl bg-white p-5 ring-1 ring-neutral-950/5">
            <p className="text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              Live URL
            </p>
            {preview ? (
              <>
                <pre className="mt-3 whitespace-pre-wrap break-all rounded-md bg-neutral-50 p-3 font-mono text-xs text-neutral-900 ring-1 ring-neutral-950/5">
                  {preview}
                </pre>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button type="button" size="sm" onClick={onCopy}>
                    <i className="fa-regular fa-copy text-xs" aria-hidden="true" />
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                  <Button type="button" size="sm" variant="ghost" asChild>
                    <a href={preview} target="_blank" rel="noopener noreferrer">
                      Open
                      <i
                        className="fa-regular fa-arrow-up-right-from-square text-xs"
                        aria-hidden="true"
                      />
                    </a>
                  </Button>
                </div>
              </>
            ) : (
              <p className="mt-3 text-sm text-neutral-600">
                Fill in the Base URL on the left — your tagged link lands here.
              </p>
            )}
          </div>
          {liveScore ? (
            <div className="rounded-2xl bg-white p-5 ring-1 ring-neutral-950/5">
              <p className="text-[11px] font-bold uppercase tracking-wide text-neutral-500">
                Taxonomy score
              </p>
              <div className="mt-3 flex items-baseline gap-3">
                <span className="font-display text-4xl font-bold tabular-nums text-neutral-950">
                  {liveScore.score}
                  <span className="text-base font-semibold text-neutral-400">/100</span>
                </span>
                <span
                  className={
                    "rounded-full px-2 py-0.5 text-xs font-semibold " +
                    (liveScore.band === "excellent" || liveScore.band === "good"
                      ? "bg-green-50 text-green-700"
                      : liveScore.band === "needs-work"
                        ? "bg-yellow-50 text-yellow-700"
                        : "bg-red-50 text-red-700")
                  }
                >
                  {liveScore.band === "excellent"
                    ? "Excellent"
                    : liveScore.band === "good"
                      ? "Good"
                      : liveScore.band === "needs-work"
                        ? "Needs work"
                        : "Broken"}
                </span>
              </div>
              {liveScore.issues.length > 0 ? (
                <ul role="list" className="mt-3 space-y-1 text-xs text-neutral-600">
                  {liveScore.issues.slice(0, 3).map((iss) => (
                    <li key={iss.label}>
                      <span className="font-medium text-neutral-800">{iss.label}</span> —{" "}
                      {iss.detail}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
          <p className="text-xs text-neutral-500">
            Need to see which rows in your live traffic are mis-tagged?{" "}
            <Link to="/app/utm-health" className="font-medium text-purple-700 hover:underline">
              Open Tag health →
            </Link>
          </p>
          <p className="text-xs text-neutral-500">
            New to the shape?{" "}
            <Link to="/app/taxonomy" className="font-medium text-purple-700 hover:underline">
              Read the canonical taxonomy →
            </Link>
          </p>
        </aside>
      </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save preset</DialogTitle>
            <DialogDescription>
              Presets are per-user. Save common link shapes so you can re-tag without re-typing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label htmlFor="preset-name" className="text-sm font-medium text-neutral-800">
              Preset name
            </label>
            <input
              id="preset-name"
              name="preset-name"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder="Spring launch — Instagram"
              className="w-full rounded-md bg-white px-3 py-2 text-sm ring-1 ring-neutral-950/10 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            {saveError ? <p className="text-sm text-red-700">{saveError}</p> : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setSaveOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={onSavePreset} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function FormRow({
  label,
  htmlFor,
  required,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-neutral-800">
        {label}
        {required ? <span className="ml-0.5 text-red-600">*</span> : null}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

// Inline pill stack rendered below an input. Each pill is one-click:
// click applies the rewrite when the hit carries a `rewriteHint`,
// otherwise the pill is informational only (vocabulary / anti-pattern
// hits don't auto-fix because there's no mechanical replacement).
function FieldPills<F extends string>({
  field,
  hits,
  onApply,
}: {
  field: F;
  hits: RuleHit[];
  onApply: (field: F, hit: RuleHit) => void;
}) {
  if (hits.length === 0) return null;
  return (
    <ul role="list" className="mt-1.5 flex flex-wrap gap-1.5">
      {hits.map((hit) => {
        const tone =
          hit.category === "formatting"
            ? "bg-sky-50 text-sky-700 ring-sky-200"
            : hit.category === "vocabulary"
              ? "bg-violet-50 text-violet-700 ring-violet-200"
              : "bg-pink-50 text-pink-700 ring-pink-200";
        const clickable = !!hit.rewriteHint;
        return (
          <li key={`${hit.category}:${hit.key}`}>
            <button
              type="button"
              onClick={clickable ? () => onApply(field, hit) : undefined}
              disabled={!clickable}
              title={hit.detail}
              className={
                "rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 transition " +
                tone +
                (clickable ? " cursor-pointer hover:brightness-95" : " cursor-default opacity-90")
              }
            >
              {clickable ? (
                <>
                  <i className="fa-regular fa-wand-magic-sparkles mr-1" aria-hidden="true" />
                  Fix → {hit.rewriteHint!.split(" -> ")[1]}
                </>
              ) : (
                <>{hit.label}</>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
